/**
 * Barra de metadados do compositor, no formato de `flashcard.pen#mETDb`:
 * uma faixa de pílulas (baralho, tags, acrescentar, criar reverso).
 * O pai é a fonte de baralho, tags e criarReverso. Só o rascunho da tag fica aqui.
 */
import { useId, useState } from 'react';
import { Layers, Plus, Tag, X } from 'lucide-react';

export interface MetadadosBarProps {
  baralho: string;
  tags: readonly string[];
  criarReverso: boolean;
  /** False na oclusão: o interruptor aparece desabilitado. */
  reversoDisponivel: boolean;
  /** False na edição: o interruptor nem é desenhado. */
  mostrarReverso: boolean;
  sugestoesBaralho?: readonly string[];
  onBaralhoChange: (baralho: string) => void;
  onTagsChange: (tags: string[]) => void;
  onCriarReversoChange: (criar: boolean) => void;
}

function sugestoesVisiveis(nomes: readonly string[] | undefined): string[] {
  const vistas = new Set<string>();
  const lista: string[] = [];
  for (const nome of nomes ?? []) {
    const limpo = nome.trim();
    if (!limpo || vistas.has(limpo)) continue;
    vistas.add(limpo);
    lista.push(limpo);
  }
  return lista;
}

const PILULA =
  'inline-flex max-w-full items-center gap-1.5 rounded-full border border-edge bg-surface px-3 py-1.5 text-xs font-medium text-ink';

export default function MetadadosBar(props: MetadadosBarProps) {
  const {
    baralho,
    tags,
    criarReverso,
    reversoDisponivel,
    mostrarReverso,
    sugestoesBaralho,
    onBaralhoChange,
    onTagsChange,
    onCriarReversoChange,
  } = props;

  const [rascunho, setRascunho] = useState('');
  const baseId = useId();
  const baralhoId = `${baseId}-baralho`;
  const tagId = `${baseId}-tag`;
  const listaId = `${baseId}-sugestoes`;
  const reversoId = `${baseId}-reverso`;
  const reversoNomeId = `${baseId}-reverso-nome`;
  const dicaId = `${baseId}-reverso-dica`;
  const sugestoes = sugestoesVisiveis(sugestoesBaralho);
  const reversoLigado = reversoDisponivel && criarReverso;

  const acrescentar = () => {
    const nova = rascunho.trim();
    if (!nova || tags.includes(nova) || nova === baralho.trim()) return;
    onTagsChange([...tags, nova]);
    setRascunho('');
  };

  const dicaReverso = reversoDisponivel
    ? 'Cria também o cartão com a frente e o verso trocados.'
    : 'Oclusão não tem duas faces de texto para inverter.';

  return (
    <div
      role="group"
      aria-label="Metadados do cartão"
      data-metadados-bar=""
      className="flex flex-wrap items-center gap-2 rounded-card border border-edge bg-surface-muted px-3 py-2.5 md:gap-3 md:px-4"
    >
      <label htmlFor={baralhoId} className={`${PILULA} w-full sm:w-auto`}>
        <Layers size={13} className="shrink-0 text-ink-secondary" aria-hidden="true" />
        <span className="sr-only">Baralho</span>
        <input
          id={baralhoId}
          type="text"
          value={baralho}
          list={listaId}
          placeholder="Baralho"
          onChange={(event) => onBaralhoChange(event.target.value)}
          className="w-full min-w-[7rem] bg-transparent text-xs font-medium text-ink placeholder:text-ink-muted focus:outline-none sm:w-40"
        />
      </label>
      <datalist id={listaId}>
        {sugestoes.map((nome) => (
          <option key={nome} value={nome} />
        ))}
      </datalist>

      <span className="hidden h-5 w-px shrink-0 bg-edge sm:block" aria-hidden="true" />
      <Tag size={14} className="hidden shrink-0 text-ink-muted sm:inline" aria-hidden="true" />

      {tags.map((tag, index) => (
        <span key={`${tag}-${index}`} className={PILULA}>
          <span className="min-w-0 break-words">{tag}</span>
          <button
            type="button"
            aria-label={`Remover tag ${tag}`}
            onClick={() => onTagsChange(tags.filter((item) => item !== tag))}
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      ))}

      <span className={`${PILULA} w-full border-dashed sm:w-auto`}>
        <button
          type="button"
          onClick={acrescentar}
          aria-label="Acrescentar tag"
          className="inline-flex shrink-0 text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60"
        >
          <Plus size={12} aria-hidden="true" />
        </button>
        <label htmlFor={tagId} className="sr-only">
          Tag
        </label>
        <input
          id={tagId}
          type="text"
          value={rascunho}
          autoComplete="off"
          placeholder="tag"
          onChange={(event) => setRascunho(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
            event.preventDefault();
            acrescentar();
          }}
          className="min-w-0 flex-1 bg-transparent text-xs text-ink placeholder:text-ink-muted focus:outline-none sm:w-28 sm:flex-none"
        />
      </span>

      {mostrarReverso ? (
        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          <span className="flex min-w-0 flex-col">
            <span
              id={reversoNomeId}
              className={`text-xs ${reversoDisponivel ? 'text-ink-secondary' : 'text-ink-muted'}`}
            >
              Criar reverso
            </span>
            <span id={dicaId} className="text-xs text-ink-muted">
              {dicaReverso}
            </span>
          </span>
          <button
            type="button"
            id={reversoId}
            role="switch"
            data-criar-reverso=""
            aria-checked={reversoLigado}
            aria-labelledby={reversoNomeId}
            aria-describedby={dicaId}
            disabled={!reversoDisponivel}
            onClick={() => onCriarReversoChange(!criarReverso)}
            className={`relative h-5 w-8 shrink-0 rounded-full border border-edge ${
              reversoLigado ? 'bg-brand' : 'bg-surface'
            } disabled:cursor-not-allowed disabled:opacity-60`}
          >
            <span
              className={`absolute top-0.5 h-3.5 w-3.5 rounded-full ${
                reversoLigado ? 'right-0.5 bg-brand-contrast' : 'left-0.5 bg-ink-muted'
              }`}
            />
          </button>
        </div>
      ) : null}
    </div>
  );
}
